import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import API from '../../services/API'
import SmallModal from '../Modals/SmallModal'
import { Delete } from '@mui/icons-material'

const getFileType = (url) => {
  const cleanUrl = url.split('?')[0].toLowerCase()
  if (cleanUrl.endsWith('.pdf')) return 'pdf'
  if (cleanUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) return 'image'
  if (cleanUrl.match(/\.(mp4|mov|m4v|webm)$/)) return 'video'
  return 'other'
}

const AttachmentPreviewModal = ({ open, onClose, s3key, onDelete }) => {
  const [attachment, setAttachment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const [openDelete, setOpenDelete] = useState(false)

  useEffect(() => {
    if (!s3key) return

    API.post('/gets3downloadUrl', {
      key: s3key,
      inlineView: true,
      // Videos stream/seek against the signed URL for the whole watch, so
      // the default 60s expiry cuts playback off partway through
      ...(getFileType(s3key) === 'video' && { expires: 3600 }),
    })
      .then((res) => res.status === 200 && setAttachment(res.data))
      .catch((e) => {
        console.error(e)
        setFetchError(true)
      })
      .finally(() => setLoading(false))
  }, [s3key])

  const fileType = s3key ? getFileType(s3key) : null

  if (!s3key) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" gap={1}>
          <>Preview</>
          <ButtonGroup>
            {onDelete && (
              <IconButton
                aria-label="delete"
                onClick={() => setOpenDelete(true)}
              >
                <Delete />
              </IconButton>
            )}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </ButtonGroup>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && <Skeleton />}
        {fetchError && (
          <Alert
            severity="error"
            title="Sorry, there was an issue viewing this file."
          />
        )}
        {fileType === 'image' && (
          <img
            src={attachment}
            alt="Attachment"
            style={{ width: '100%', height: 'auto', borderRadius: 4 }}
          />
        )}

        {fileType === 'video' && (
          <video
            src={attachment}
            controls
            playsInline
            style={{ width: '100%', maxHeight: '75vh', borderRadius: 4 }}
          />
        )}

        {fileType === 'pdf' && (
          <iframe
            src={attachment}
            title="PDF Preview"
            style={{ width: '100%', height: '80vh', border: 'none' }}
          />
        )}

        {fileType === 'other' && (
          <p>Preview not available for this file type.</p>
        )}
        <SmallModal open={openDelete} onClose={() => setOpenDelete(false)}>
          <Stack gap={1}>
            <Typography>Are you sure you want to delete this file?</Typography>
            <Button
              onClick={() => {
                onDelete(s3key)
                setOpenDelete(false)
                onClose()
              }}
              variant="outlined"
              color="anRed"
            >
              Yes - Delete
            </Button>
            <Button
              onClick={() => setOpenDelete(false)}
              variant="contained"
              color="anRed"
            >
              No - Cancel
            </Button>
          </Stack>
        </SmallModal>
      </DialogContent>
    </Dialog>
  )
}

export default AttachmentPreviewModal
